import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const inquirySchema = z.object({
  first_name: z.string().trim().min(1).max(100),
  last_name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(50).optional().default(""),
  event_type: z.string().max(60).optional().default(""),
  preferred_dates: z.string().max(200).optional().default(""),
  dates_flexible: z.string().max(40).optional().default(""),
  estimated_overnight_guests: z.string().max(40).optional().default(""),
  estimated_total_onsite: z.string().max(40).optional().default(""),
  nights_desired: z.string().max(40).optional().default(""),
  full_property_buyout: z.string().max(40).optional().default(""),
  food_plan: z.string().max(100).optional().default(""),
  onsite_activities: z.array(z.string().max(60)).max(20).optional().default([]),
  offsite_activities: z.array(z.string().max(60)).max(20).optional().default([]),
  has_coordinator: z.string().max(40).optional().default(""),
  needs_rentals: z.string().max(40).optional().default(""),
  needs_transport: z.string().max(40).optional().default(""),
  budget_range: z.string().max(60).optional().default(""),
  priorities: z.string().max(2000).optional().default(""),
  notes: z.string().max(3000).optional().default(""),
});

const NOTIFY_TO = "sales@back.land";
const NOTIFY_CC = ["jeremy@back.land", "mark@back.land"];

type LooseClient = {
  from: (t: string) => any;
};

const SENDER_DOMAIN = "notify.travelbackland.com";
const FROM_DOMAIN = "travelbackland.com";
const SITE_NAME = "Backland";

async function logSend(
  admin: LooseClient,
  templateName: string,
  recipient: string,
  status: "sent" | "suppressed" | "failed",
  errorMessage?: string,
) {
  const { error } = await admin.from("email_send_log").insert({
    template_name: templateName,
    recipient_email: recipient,
    status,
    error_message: errorMessage ?? null,
  });
  if (error) {
    console.error("Failed to write email_send_log row", { status, error });
  }
}

async function sendGroupsInquiryEmails(data: z.infer<typeof inquirySchema>) {
  try {
    const [adminMod, { sendTemplateEmail }] = await Promise.all([
      import("@/integrations/supabase/client.server"),
      import("@/lib/email-templates/send-email"),
    ]);
    // Generated types don't include email log tables; cast for flexibility.
    const admin = adminMod.supabaseAdmin as unknown as LooseClient;

    const templateName = "groups-inquiry-notification";
    const inquiryKey = `${data.email}-${Date.now()}`;
    const recipients = [NOTIFY_TO, ...NOTIFY_CC];

    await Promise.all(
      recipients.map(async (recipient) => {
        try {
          const result = await sendTemplateEmail(templateName, recipient, {
            templateData: data as Record<string, unknown>,
            idempotencyKey: `groups-${inquiryKey}-${recipient}`,
            replyTo: data.email,
          });
          if (result.sent) {
            await logSend(admin, templateName, recipient, "sent");
          } else {
            await logSend(admin, templateName, recipient, "suppressed");
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          console.error("Failed to send groups inquiry email", {
            recipient,
            message,
          });
          await logSend(admin, templateName, recipient, "failed", message);
        }
      }),
    );

    // Guest confirmation email — sent directly so the guest-facing From address
    // ("Backland Groups <sales@...>") is preserved exactly as before.
    const confirmationName = "groups-inquiry-confirmation";
    try {
      const [React, { render }, confirmationMod, { sendLovableEmail, EmailAPIError }] =
        await Promise.all([
          import("react"),
          import("@react-email/render"),
          import("@/lib/email-templates/groups-inquiry-confirmation"),
          import("@lovable.dev/email-js"),
        ]);

      const apiKey = process.env["LOVABLE_API_KEY"];
      if (!apiKey) throw new Error("LOVABLE_API_KEY is not configured");

      const confirmationTemplate = confirmationMod.template as {
        component: typeof confirmationMod.template.component;
        subject: string | ((d: Record<string, unknown>) => string);
      };
      const confirmationSubject =
        typeof confirmationTemplate.subject === "function"
          ? confirmationTemplate.subject(data as Record<string, unknown>)
          : confirmationTemplate.subject;

      const confirmationElement = React.createElement(
        confirmationTemplate.component,
        { first_name: data.first_name } as never,
      );
      const confirmationHtml = await render(confirmationElement);
      const confirmationText = await render(confirmationElement, {
        plainText: true,
      });

      try {
        await sendLovableEmail(
          {
            to: data.email,
            from: `${SITE_NAME} Groups <sales@${FROM_DOMAIN}>`,
            sender_domain: SENDER_DOMAIN,
            subject: confirmationSubject,
            html: confirmationHtml,
            text: confirmationText,
            purpose: "transactional",
            label: confirmationName,
            idempotency_key: `groups-confirm-${inquiryKey}`,
            reply_to: "sales@back.land",
          },
          { apiKey, sendUrl: process.env["LOVABLE_SEND_URL"] },
        );
        await logSend(admin, confirmationName, data.email, "sent");
      } catch (err) {
        if (err instanceof EmailAPIError && err.code === "recipient_suppressed") {
          await logSend(admin, confirmationName, data.email, "suppressed");
        } else {
          throw err;
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("Groups confirmation email failed", message);
      await logSend(admin, confirmationName, data.email, "failed", message);
    }
  } catch (err) {
    // Never let email failures break the form submission
    console.error("Groups inquiry notification email failed", err);
  }
}

export const submitEventInquiry = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => inquirySchema.parse(input))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { error } = await sb.from("event_inquiries").insert({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone || null,
      event_type: data.event_type || null,
      preferred_dates: data.preferred_dates || null,
      estimated_overnight_guests: data.estimated_overnight_guests || null,
      estimated_total_onsite: data.estimated_total_onsite || null,
      budget_range: data.budget_range || null,
      notes: data.notes || null,
      details: {
        dates_flexible: data.dates_flexible,
        nights_desired: data.nights_desired,
        full_property_buyout: data.full_property_buyout,
        food_plan: data.food_plan,
        onsite_activities: data.onsite_activities,
        offsite_activities: data.offsite_activities,
        has_coordinator: data.has_coordinator,
        needs_rentals: data.needs_rentals,
        needs_transport: data.needs_transport,
        priorities: data.priorities,
      },
    });
    if (error) throw new Error(error.message);

    await sendGroupsInquiryEmails(data);

    return { ok: true };
  });
